import * as THREE from 'three'
import Experience from '../../Experience'
import { InteractableMixin } from '../../Utils/InteractableMixin'
import { damp, dampAngle, easeInOutCubic } from './bookMath'

/**
 * Page — 本の1ページ。ボーンチェーン付き SkinnedMesh で紙の「しなり」を表現する。
 * Wawa Sensei の r3f-animated-book-slider（Page.jsx）のバニラ移植。
 */

export const PAGE_WIDTH = 1.28
export const PAGE_HEIGHT = 1.71
export const PAGE_DEPTH = 0.003
const PAGE_SEGMENTS = 30
const SEGMENT_WIDTH = PAGE_WIDTH / PAGE_SEGMENTS

// めくりカーブの強さ（元実装の定数そのまま。カール/折れは Book.curlParams で調整可能）
const INSIDE_CURVE_STRENGTH = 0.18
const OUTSIDE_CURVE_STRENGTH = 0.05
// めくりの速度は Book.turnDuration の進行度カーブが受け持つため、
// damp はボーン追従を滑らかにする弱いフィルタとしてだけ効かせる
// （大きくすると回転がカールに遅れて、設置済みページへの貫通が再発する）
const EASING_FACTOR = 0.15 // rotation.y の smoothTime（秒）
const EASING_FACTOR_FOLD = 0.15 // rotation.x（折れ）の smoothTime（秒）

// --- 全ページ共有のジオメトリ ---
// 背表紙（回転の原点）を x=0 に寄せ、各頂点を左右2本のボーンに
// 区間内位置の割合でウェイト分けするスキニング属性を手動で構築する
function createPageGeometry() {
  const geometry = new THREE.BoxGeometry(PAGE_WIDTH, PAGE_HEIGHT, PAGE_DEPTH, PAGE_SEGMENTS, 2)
  geometry.translate(PAGE_WIDTH / 2, 0, 0)

  const position = geometry.attributes.position
  const vertex = new THREE.Vector3()
  const skinIndexes = []
  const skinWeights = []

  for (let i = 0; i < position.count; i++) {
    vertex.fromBufferAttribute(position, i)
    const skinIndex = Math.max(0, Math.floor(vertex.x / SEGMENT_WIDTH))
    const skinWeight = (vertex.x % SEGMENT_WIDTH) / SEGMENT_WIDTH
    skinIndexes.push(skinIndex, skinIndex + 1, 0, 0)
    skinWeights.push(1 - skinWeight, skinWeight, 0, 0)
  }

  geometry.setAttribute('skinIndex', new THREE.Uint16BufferAttribute(skinIndexes, 4))
  geometry.setAttribute('skinWeight', new THREE.Float32BufferAttribute(skinWeights, 4))
  return geometry
}

const pageGeometry = createPageGeometry()

// 共有のエッジ材質（BoxGeometry の材質順: +x, -x, +y, -y, +z, -z）
const edgeWhiteMaterial = new THREE.MeshStandardMaterial({ color: 'white' })
const edgeSpineMaterial = new THREE.MeshStandardMaterial({ color: '#111' })

export default class Page {
  constructor({ book, number, frontTexture, backTexture }) {
    this.experience = new Experience()
    this.book = book
    this.number = number

    this.opened = false
    this.lastOpened = false
    this.highlighted = false
    this.turnedAt = 0
    this.turnFromRotation = null
    this.currentTargetRotation = null

    this.setMesh(frontTexture, backTexture)
    this.setInteraction()
  }

  setMesh(frontTexture, backTexture) {
    // 背表紙側から小口へ連なるボーンチェーン
    this.bones = []
    for (let i = 0; i <= PAGE_SEGMENTS; i++) {
      const bone = new THREE.Bone()
      bone.position.x = i === 0 ? 0 : SEGMENT_WIDTH
      if (i > 0) this.bones[i - 1].add(bone)
      this.bones.push(bone)
    }
    const skeleton = new THREE.Skeleton(this.bones)

    this.frontMaterial = new THREE.MeshStandardMaterial({
      color: 'white',
      map: frontTexture,
      roughness: 0.9,
      emissive: new THREE.Color('orange'),
      emissiveIntensity: 0,
    })
    this.backMaterial = new THREE.MeshStandardMaterial({
      color: 'white',
      map: backTexture,
      roughness: 0.9,
      emissive: new THREE.Color('orange'),
      emissiveIntensity: 0,
    })

    this.mesh = new THREE.SkinnedMesh(pageGeometry, [
      edgeWhiteMaterial, // +x 小口
      edgeSpineMaterial, // -x 背
      edgeWhiteMaterial, // +y 天
      edgeWhiteMaterial, // -y 地
      this.frontMaterial, // +z 表面
      this.backMaterial, // -z 裏面
    ])
    this.mesh.castShadow = true
    this.mesh.receiveShadow = true
    // ボーン変形は元のバウンディングを外れるためカリング無効化 + レイキャスト用の広めの境界球
    this.mesh.frustumCulled = false
    this.mesh.boundingSphere = new THREE.Sphere(new THREE.Vector3(), PAGE_WIDTH * 1.5)
    this.mesh.add(this.bones[0])
    this.mesh.bind(skeleton)

    // group.rotation.y がページ全体の開閉（bone0 の担当分）を受ける
    this.group = new THREE.Group()
    this.group.add(this.mesh)
  }

  setInteraction() {
    Object.assign(this, InteractableMixin)

    // 開いている側（左）をクリック → 1ページ戻す / 閉じている側（右）→ 1ページ進める。
    // 重なったシートはレイキャストがどれに当たるか不安定なため、シート番号へのジャンプではなく
    // 常に1ページずつめくる（閉じた本のタップで複数ページ飛ぶ誤動作の防止）
    this.onInteract = () => {
      this.book.setPage(this.book.page + (this.opened ? -1 : 1), { byUser: true })
    }
    // Mixin 既定のカーソル処理はオーバーライドで消えるため、ここで再実装している
    this.onHover = () => {
      if (!this.isHovered) {
        this.isHovered = true
        document.body.style.cursor = 'pointer'
      }
      this.highlighted = true
    }
    this.onHoverExit = () => {
      if (this.isHovered) {
        this.isHovered = false
        document.body.style.cursor = 'default'
      }
      this.highlighted = false
    }

    this.makeInteractable()
  }

  update(dtSeconds) {
    if (this.lastOpened !== this.opened) {
      this.turnedAt = Date.now()
      // めくり途中で切り返されても現在の角度から滑らかに続くよう、開始角を記録
      this.turnFromRotation =
        this.currentTargetRotation ?? (this.opened ? Math.PI / 2 : -Math.PI / 2)
      this.lastOpened = this.opened
    }
    // めくりの進行度（0→1）。回転もカールもこれに同期して動く
    const progress =
      this.turnedAt === 0
        ? 1
        : Math.min(1, (Date.now() - this.turnedAt) / 1000 / this.book.turnDuration)
    const eased = easeInOutCubic(progress)
    // めくり中だけ効くベル型カーブ。着地の手前（eased≈0.8）でカールを先に解消し、
    // 最後は平らな紙として下のページへ自然に重なる（貫通防止）
    const curlWindow = Math.sin(Math.min(1, eased * 1.25) * Math.PI)
    // 折れはさらに短く効かせる（裾を2乗で絞る）
    const foldWindow = curlWindow * curlWindow

    const emissiveTarget = this.highlighted ? 0.22 : 0
    this.frontMaterial.emissiveIntensity = damp(
      this.frontMaterial.emissiveIntensity, emissiveTarget, 0.1, dtSeconds
    )
    this.backMaterial.emissiveIntensity = this.frontMaterial.emissiveIntensity

    const bookClosed = this.book.bookClosed
    // 開いている間はページ番号でわずかに扇状に広げる
    const fan = bookClosed ? 0 : THREE.MathUtils.degToRad(this.number * 0.8)
    const toRotation = (this.opened ? -Math.PI / 2 : Math.PI / 2) + fan
    // 進行度で開始角から着地角へ補間する（turnedAt === 0 の初期ページは即着地角）
    const targetRotation = THREE.MathUtils.lerp(
      this.turnFromRotation ?? toRotation, toRotation, eased
    )
    this.currentTargetRotation = targetRotation

    const bones = this.bones
    for (let i = 0; i < bones.length; i++) {
      // bone0 の担当分はページの Group が受ける（元実装と同じ分割）
      const target = i === 0 ? this.group : bones[i]

      const insideCurveIntensity = i < 8 ? Math.sin(i * 0.2 + 0.25) : 0
      const outsideCurveIntensity = i >= 8 ? Math.cos(i * 0.3 + 0.09) : 0
      const turningIntensity = Math.sin((i * Math.PI) / bones.length) * curlWindow

      // カール項は着地角に比例させる（補間中の targetRotation は 0 をまたぐ瞬間があり、
      // そこでカールが消えると平らな紙が設置済みページを貫通してしまう）
      let rotationAngle =
        INSIDE_CURVE_STRENGTH * insideCurveIntensity * targetRotation -
        OUTSIDE_CURVE_STRENGTH * outsideCurveIntensity * targetRotation +
        this.book.curlParams.turning * turningIntensity * toRotation
      // 折れの向きは着地側で固定（補間中の targetRotation は 0 をまたいで符号が変わるため）
      let foldRotationAngle = THREE.MathUtils.degToRad(
        Math.sign(toRotation) * this.book.curlParams.foldDeg
      )

      if (bookClosed || this.book.flatMode) {
        // 平らに保つ（完全に閉じた本 / flatMode）: 回転を group に集約し、曲げ・折れは使わない
        if (i === 0) {
          rotationAngle = targetRotation
        } else {
          rotationAngle = 0
        }
        foldRotationAngle = 0
      }

      target.rotation.y = dampAngle(target.rotation.y, rotationAngle, EASING_FACTOR, dtSeconds)

      // めくり中だけ外側のボーンに斜めの「折れ」を加える
      const foldIntensity =
        i > 8 ? Math.sin((i * Math.PI) / bones.length - 0.5) * foldWindow : 0
      target.rotation.x = dampAngle(
        target.rotation.x, foldRotationAngle * foldIntensity, EASING_FACTOR_FOLD, dtSeconds
      )
    }

    // 重なり順: めくるたびにスタックが組み変わり、開いた見開きが中央で揃う
    this.mesh.position.z = -this.number * PAGE_DEPTH + this.book.delayedPage * PAGE_DEPTH
  }
}
